import React, { useEffect, useState } from "react";
import { api } from "../../app/Api/ApiSlice";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaMinus, FaPlus } from "react-icons/fa";
import "./storesStyles.css";

const Cart = () => {
    const [cartDetails, setCartDetails] = useState({ products: [], items: [] });
    const [isLoading, setIsLoading] = useState(false);

    const { cart, name } = useParams();

    // Fetch cart on mount
    useEffect(() => {
        const getCart = async () => {
            try {
                setIsLoading(true);
                const response = await api.get(`/stores/carts/${cart}`);
                if (response?.data) {
                    setCartDetails(response.data);
                } else {
                    toast.info("Cart not found");
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || "Failed to load cart");
            } finally {
                setIsLoading(false);
            }
        };
        getCart();
    }, [cart]);

    // Update product quantity in cart
    const updateQuantity = async (productId, action) => {
        try {
            const response = await api.patch(
                `/stores/carts/${cart}/items/${productId}`,
                { action }
            );

            if (response) {
                setCartDetails((prev) => {
                    const updatedProducts = prev.products
                        .map((product) => {
                            if (product.id._id === productId) {
                                let newQuantity =
                                    action === "increase"
                                        ? product.quantity + 1
                                        : product.quantity - 1;

                                // Prevent quantity from dropping below 1
                                newQuantity = Math.max(newQuantity, 1);

                                return { ...product, quantity: newQuantity };
                            }
                            return product;
                        });

                    return {
                        ...prev,
                        products: updatedProducts,
                        items: updatedProducts // keep items count in sync
                    };
                });

                toast.success("Cart updated");
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to update cart"
            );
        }
    };

    // Calculate subtotal for a single product
    const calcSubTotal = (product) => product.id.price * product.quantity;

    // Calculate total for the cart
    const calcTotal = () =>
        cartDetails?.products?.reduce(
            (acc, product) => acc + product.id.price * product.quantity,
            0
        );

    // Edit cart name
    const editCart = async (newName) => {
        try {
            const response = await api.patch(`/stores/carts/${cart}`, {
                name: newName
            });
            if (response?.data) {
                setCartDetails(response.data);
                toast.success("Cart updated successfully");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to edit cart");
        }
    };

    // Send purchase order
    const sendPurchaseOrder = async () => {
        try {
            cartDetails.cart = cartDetails._id
            const response = await api.post(`/stores/orders`, { cartDetails });
            if (response) {
                toast.success("Purchase order sent successfully");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to send order");
        }
    };

    return (
        <div className="store-carts-container">
            <h2 className="page-title">{name} - Cart</h2>

            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <>
                    <div className="cart-details">
                        <div className="cart-details-info">
                            <strong>Cart name:</strong> <span>{cartDetails.name}</span>
                        </div>
                        <div className="cart-details-info">
                            <strong>Items:</strong>{" "}
                            <span>{cartDetails.items?.length || 0}</span>
                        </div>
                        <div className="cart-details-info">
                            <strong>Orders:</strong>{" "}
                            <span>
                                <Link to={`/carts/${cart}/orders`}>
                                    {cartDetails.ordersCount || 0}
                                </Link>
                            </span>
                        </div>
                    </div>

                    <div className="table-wrapper">
                        <table className="carts-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Name</th>
                                    <th>Price</th>
                                    <th>Quantity</th>
                                    <th>Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cartDetails?.products?.map((product) => (
                                    <tr key={product.id._id}>
                                        <td>
                                            <img
                                                src={product.id?.images[0]?.url}
                                                alt={product.id.title}
                                                width={50}
                                                height={50}
                                                style={{ borderRadius: "6px" }}
                                            />
                                        </td>
                                        <td>{product.id.title}</td>
                                        <td>${product.id.price}</td>
                                        <td>
                                            <button
                                                className="qty-btn"
                                                onClick={() =>
                                                    updateQuantity(product.id._id, "decrease")
                                                }
                                            >
                                                <FaMinus />
                                            </button>
                                            <span className="qty-value">{product.quantity}</span>
                                            <button
                                                className="qty-btn"
                                                onClick={() =>
                                                    updateQuantity(product.id._id, "increase")
                                                }
                                            >
                                                <FaPlus />
                                            </button>
                                        </td>
                                        <td className="cart-total">${calcSubTotal(product)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan="4">
                                        <strong>Total</strong>
                                    </td>
                                    <td className="cart-total">
                                        <strong>${calcTotal()}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="cart-actions">
                        <button
                            className="view-btn"
                            onClick={() => editCart("New Cart Name")}
                        >
                            Edit Cart
                        </button>
                        <button className="view-btn" onClick={sendPurchaseOrder}>
                            Send Purchase Order
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Cart;